import {Component, Input, inject, EventEmitter, Output, ChangeDetectorRef, NgZone} from '@angular/core';
import {CommonModule, NgIf, NgFor} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonSize, ButtonState, ButtonType, IApplication} from "../../../assets/types/dtoTypes";
import {JobsDetailsService} from "../../pages/main-pages/company/jobs-details/jobs-details.service";
import {InterviewDialogComponent} from "../../modals/interview-modal/interview-modal.component";
import {PATH} from "../../../assets/constants";
import {ButtonComponent} from "../button/button.component";

type Variant = 'menu' | 'buttons';
type Role = 'company' | 'candidate';

type ActionKey =
  'message' |
  'shortlist' | 'decline' | 'invite' | 'offer' | 'reschedule' | 'hire' | 'offered' | 'revert' |
  'withdraw' | 'join' | 'accept';

interface ActionDef {
  key: ActionKey;
  label: string;
  color?: 'primary' | 'accent' | 'warn';
  appearance?: 'primary' | 'secondary' | 'ghost';
  icon?: string;
  asset?: string;
}

const PREV_STATUS: Record<string, string> = {
  'SHORTLISTED'          : 'PENDING',
  'INVITED_TO_INTERVIEW' : 'SHORTLISTED',
  'INTERVIEWED'          : 'INVITED_TO_INTERVIEW',
  'OFFERED'              : 'INTERVIEWED',
  'ACCEPTED'             : 'OFFERED',
  'REJECTED'             : 'PENDING',
};

@Component({
  selector: 'app-application-actions',
  standalone: true,
  imports: [CommonModule, NgIf, NgFor, MatButtonModule, MatMenuModule, MatIconModule, MatTooltipModule, TranslateModule, ButtonComponent],
  templateUrl: './app-application-actions.component.html',
  styleUrls: ['./app-application-actions.component.scss'],
})
export class ApplicationActionsComponent {

  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  @Input({required: true}) app!: IApplication;
  @Input() isMobile = false;
  @Input() jobOfferId: string;
  @Input() userId: string;
  @Input() variant: Variant = 'buttons';
  @Input() role: Role = 'company';
  @Output() actionCompleted = new EventEmitter<{ kind:
      'invited'|'offered'|'hired'|'declined'|'withdrawn'|'joined'|'reschedule'|'reverted',
    app: IApplication }>();

  private router = inject(Router);
  private jobsDetailsService = inject(JobsDetailsService);
  private dialog = inject(MatDialog);

  get isDecisionMade(): boolean {
    return this.app?.status === 'ACCEPTED' || this.app?.status === 'REJECTED';
  }

  get hasActiveInterview(): boolean {
    const d = this.app?.interviewDate;
    const t = this.app?.interviewTime;
    if (!d || !t) return false;
    const [h='00', m='00', s='00'] = t.split(':');
    const start = new Date(`${d}T${h.padStart(2,'0')}:${m.padStart(2,'0')}:${(s||'00').padStart(2,'0')}`);
    return isFinite(start.getTime()) && start.getTime() > Date.now();
  }

  get canJoin(): boolean {
    if (this.app?.status !== 'INVITED_TO_INTERVIEW') return false;
    const d = this.app?.interviewDate;
    const t = this.app?.interviewTime;
    if (!d || !t) return false;
    const [h='00', m='00', s='00'] = t.split(':');
    const start = new Date(`${d}T${h.padStart(2,'0')}:${m.padStart(2,'0')}:${(s||'00').padStart(2,'0')}`).getTime();
    if (!isFinite(start)) return false;
    const now = Date.now();
    return now >= (start - 10 * 60 * 1000) && now <= (start + 2 * 60 * 60 * 1000);
  }

  get previousStatus(): string | null {
    return PREV_STATUS[this.app?.status] ?? null;
  }

  /** All visible actions for the current role/status */
  get visibleActions(): ActionDef[] {
    return (this.role === 'company')
      ? this.visibleCompanyActions()
      : this.visibleCandidateActions();
  }

  /** Actions that go into the three-dots menu (no message) */
  get menuActions(): ActionDef[] {
    return this.visibleActions.filter(a => a.key !== 'message');
  }

  /** Whether to show the chat icon button outside the menu */
  get showChat(): boolean {
    return this.visibleActions.some(a => a.key === 'message');
  }

  private actionsByStatusCompany: Record<string, ActionKey[]> = {
    'PENDING'              : ['shortlist', 'message', 'decline', 'revert'],
    'SHORTLISTED'          : ['message', 'invite', 'decline', 'revert'],
    'INVITED_TO_INTERVIEW' : ['message', 'invite', 'reschedule', 'offer', 'decline', 'revert'],
    'INTERVIEWED'          : ['message', 'invite', 'offer', 'decline', 'revert'],
    'OFFERED'              : ['message', 'decline', 'revert'],
    'ACCEPTED'             : ['message', 'revert'],
    'REJECTED'             : ['message', 'revert'],
    '*'                    : ['message']
  };

  private visibleCompanyActions(): ActionDef[] {
    const status = this.app?.status ?? '*';
    let keys = this.actionsByStatusCompany[status] ?? this.actionsByStatusCompany['*'];

    keys = keys.filter(k => {
      if (k === 'invite' || k === 'offer' || k === 'decline') return !this.isDecisionMade;
      if (k === 'reschedule') return this.hasActiveInterview;
      if (k === 'hire') return !this.isDecisionMade && this.app?.status === 'OFFERED';
      if (k === 'revert') return !!this.previousStatus;
      return true;
    });

    return keys.map(k => this.allActions[k]);
  }

  private actionsByStatusCandidate: Record<string, ActionKey[]> = {
    'INVITED_TO_INTERVIEW': ['join', 'message'],
    'OFFERED'             : ['accept', 'decline', 'message'],
    '*': ['message']
  };

  private visibleCandidateActions(): ActionDef[] {
    const status = this.app?.status ?? '*';
    let keys = this.actionsByStatusCandidate[status] ?? this.actionsByStatusCandidate['*'];

    keys = keys.filter(k => {
      if (k === 'join') return this.canJoin;
      if (k === 'reschedule') return this.app?.status === 'INVITED_TO_INTERVIEW';
      if (k === 'withdraw') return !this.isDecisionMade;
      return true;
    });

    return keys.map(k => this.allActions[k]);
  }

  private allActions: Record<ActionKey, ActionDef> = {
    shortlist:  {key: 'shortlist',  label: 'applications.menu.shortlist',       color: 'primary', appearance: 'primary',   icon: 'star',                 asset: 'assets/star'},
    decline:    {key: 'decline',    label: 'applications.menu.decline',         color: 'warn',    appearance: 'secondary', icon: 'thumb_down',           asset: 'assets/decline.png'},
    invite:     {key: 'invite',     label: 'applications.menu.inviteInterview', color: 'primary', appearance: 'primary',   icon: 'event',                asset: 'assets/joininterview.png'},
    offer:      {key: 'offer',      label: 'applications.menu.offer',           color: 'primary', appearance: 'primary',   icon: 'local_offer',          asset: 'assets/event'},
    reschedule: {key: 'reschedule', label: 'applications.menu.reschedule',                        appearance: 'primary',   icon: 'schedule',             asset: 'assets/event'},
    offered:    {key: 'offered',    label: 'applications.menu.offered',                           appearance: 'primary',   icon: 'flag',                 asset: 'assets/event'},
    hire:       {key: 'hire',       label: 'applications.menu.hire',            color: 'primary', appearance: 'primary',   icon: 'assignment_turned_in', asset: 'assets/event'},
    revert:     {key: 'revert',     label: 'applications.menu.revert',                            appearance: 'ghost',     icon: 'undo',                 asset: 'assets/event'},
    message:    {key: 'message',    label: 'applications.menu.message',                           appearance: 'primary',   icon: 'chat',                 asset: 'assets/bubble.png'},
    withdraw:   {key: 'withdraw',   label: 'applications.menu.decline',         color: 'warn',    appearance: 'secondary', icon: 'logout',               asset: 'assets/decline.png'},
    join:       {key: 'join',       label: 'applications.menu.joinInterview',   color: 'primary', appearance: 'primary',   icon: 'video_call',           asset: 'assets/event'},
    accept:     {key: 'accept',     label: 'applications.menu.accept',          color: 'primary', appearance: 'primary',   icon: 'check_circle',         asset: 'assets/event'},
  };

  handle(action: ActionKey) {
    const app = this.app;

    if (action === 'message') {
      if (this.role !== 'company') {
        this.router.navigate([PATH.CHAT, this.jobOfferId]);
      } else {
        this.router.navigate([PATH.CHAT, this.userId]);
      }
      return;
    }

    if (action === 'accept') {
      if (this.role === 'candidate') {
        this.jobsDetailsService.candidateAccept(app.id).subscribe(() => app.status = 'ACCEPTED');
      } else {
        this.jobsDetailsService.accept(app.id).subscribe(() => app.status = 'ACCEPTED');
      }
      return;
    }

    if (action === 'revert') {
      const prev = this.previousStatus;
      if (!prev) return;
      this.jobsDetailsService.revertStatus(app.id, prev).subscribe(() => {
        this.ngZone.run(() => {
          app.status = prev as IApplication['status'];
          this.cdr.markForCheck();
          this.actionCompleted?.emit({ kind: 'reverted', app });
        });
      });
      return;
    }

    if (this.role === 'company') {
      switch (action) {
        case 'shortlist':
          this.jobsDetailsService.shortlist(app.id).subscribe(() => app.status = 'SHORTLISTED');
          break;
        case 'decline':
          this.jobsDetailsService.reject(app.id).subscribe(() => app.status = 'REJECTED');
          break;
        case 'invite':
          this.openInterviewDialog(app);
          break;
        case 'reschedule':
          this.openInterviewDialog(app, {reschedule: true});
          break;
        case 'offer':
          this.jobsDetailsService.offer(app.id).subscribe(() => {
            app.status = 'OFFERED';
            this.actionCompleted?.emit({ kind: 'offered', app });
          });
          break;
      }
      return;
    }

    switch (action) {
      case 'withdraw':
      case 'decline':
        this.jobsDetailsService.candidateRefuse(app.id).subscribe?.(() => {
          app.status = 'REJECTED';
          this.actionCompleted.emit({ kind: 'withdrawn', app });
        });
        break;
      case 'join':
        this.router.navigate([PATH.VIDEO, app.id]);
        this.actionCompleted.emit({ kind: 'joined', app });
        break;
      case 'reschedule':
        this.openInterviewDialog(app, { reschedule: true });
        this.actionCompleted.emit({ kind: 'reschedule', app });
        break;
    }
  }

  private openInterviewDialog(app: IApplication, opts?: { reschedule?: boolean }) {
    const dialogRef = this.dialog.open(InterviewDialogComponent, {data: {app, ...opts}});
    dialogRef.afterClosed().subscribe((result: {
      title: string;
      interviewDate: string;
      startTime: string;
      endTime: string;
      guests: { guestEmail: string; guestName: string; guestId: string }[];
    }) => {
      if (!result) return;

      const safeGuests = (result.guests ?? []).filter(g => g.guestName && g.guestEmail);
      if (safeGuests.length === 0) {
        safeGuests.push({guestName: 'mahdi', guestEmail: 'mahdi.feki@gmx.de', guestId: 'temp-guest'});
      }

      const toHms = (t: string) => {
        const [h = '00', m = '00', s = '00'] = t.split(':');
        return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:${s.padStart(2, '0')}`;
      };

      const startHms = toHms(result.startTime);
      const endHms = toHms(result.endTime);

      const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      };
      if (toMinutes(startHms) >= toMinutes(endHms)) {
        console.warn('Start time must be before end time');
        return;
      }

      this.jobsDetailsService
        .inviteToInterview(app.id, result.interviewDate, startHms, endHms, safeGuests, result.title)
        .subscribe(() => {
          this.ngZone.run(() => {
            app.status = 'INVITED_TO_INTERVIEW';
            app.interviewDate = result.interviewDate as any;
            app.interviewTime = startHms as any;
            this.cdr.markForCheck();
          });
        });
    });
  }

  protected readonly ButtonType = ButtonType;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonSize = ButtonSize;
}
